import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import type { CustomerThunkAction } from '@jane/monolith/customer/customerAction';
import type { AppState } from '@jane/monolith/customer/redux';
import { StoreSource } from '@jane/monolith/sources/store';
import { getOrderingSchedule } from '@jane/shared-ecomm/util';
import type { OrderingSchedule } from '@jane/shared-ecomm/util';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';

import { initiateVerifyStore, verifyStoreSuccess } from './embeddedApp';

interface GetStoreParams {
  id: string | number;
  params?: object;
}
export const getStore =
  ({ id, params }: GetStoreParams): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateGetStore());
    StoreSource.get(id, params).then(
      (result) => dispatch(getStoreSuccess(result)),
      (err) => dispatch(getStoreError(err))
    );
  };

export type StoreState = {
  hasLoaded: boolean;
  isLoadingStore: boolean;
  store: Store | null;
};

const getInitialState = (): StoreState => ({
  hasLoaded: false,
  isLoadingStore: false,
  store: null,
});

const setLoadedStore = (state: StoreState, store: Store): StoreState => ({
  ...state,
  store,
  isLoadingStore: false,
  hasLoaded: true,
});

const storeSlice = createSlice({
  name: 'store',
  initialState: getInitialState(),
  reducers: {
    initiateGetStore: (state) => {
      return { ...state, isLoadingStore: true };
    },
    getStoreSuccess: (state, action: PayloadAction<{ store: Store }>) => {
      return setLoadedStore(state, action.payload.store);
    },
    getStoreError: (state, _action: PayloadAction<unknown>) => {
      return { ...state, isLoadingStore: false };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initiateVerifyStore.type, (state) => {
      return { ...state, isLoadingStore: true };
    });
    builder.addCase(verifyStoreSuccess, (state, action) => {
      return setLoadedStore(state, action.payload.store);
    });
  },
});

export const {
  reducer: storeReducer,
  actions: { initiateGetStore, getStoreSuccess, getStoreError },
} = storeSlice;

export type StoreActions =
  | ReturnType<typeof getStoreError>
  | ReturnType<typeof getStoreSuccess>
  | ReturnType<typeof initiateGetStore>;

interface StoreStateWithSchedules extends StoreState {
  schedules: OrderingSchedule | undefined;
}

export const selectStore = createSelector(
  [(state: AppState) => state.store],
  (dispensaryState): StoreStateWithSchedules => {
    const dispensary = dispensaryState.store;

    return {
      ...dispensaryState,
      schedules: dispensary
        ? getOrderingSchedule({
            store: dispensary,
            type: ['curbside', 'delivery', 'pickup', 'retail'],
          })
        : undefined,
    };
  }
);
